import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';

// Opplane logo
import opplaneLogo from '../../assets/opplane-logo.png';

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: Enter email, 2: Enter code and new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const navigate = useNavigate();
  
  // Get API URL from environment variables - use relative path in production
  const API_URL = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:5001/api');
  
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setErrorMessage('Please enter your email address');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      
      setSuccessMessage('If an account with that email exists, we have sent a password reset code to your email address.');
      setStep(2);
    } catch (err) {
      console.error('Forgot password error:', err);
      setErrorMessage(err.response?.data?.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (!code || !newPassword || !confirmPassword) {
      setErrorMessage('Please fill in all fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }
    
    // Password validation
    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return;
    }
    
    // Check for uppercase, lowercase, number, and special character
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasLowercase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword);
    
    if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecial) {
      setErrorMessage('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      
      await axios.post(`${API_URL}/auth/confirm-forgot-password`, {
        email,
        code,
        newPassword
      });
      
      setSuccessMessage('Password has been reset successfully! You can now login with your new password.');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      setErrorMessage(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleResendCode = async () => {
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      
      setSuccessMessage('A new verification code has been sent to your email address.');
    } catch (err) {
      console.error('Resend code error:', err);
      setErrorMessage('Failed to resend verification code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <ForgotPasswordContainer>
      <ForgotPasswordCard>
        <LogoContainer>
          <OpplaneLogo src={opplaneLogo} alt="Opplane Logo" />
        </LogoContainer>
        
        {step === 1 ? (
          <EmailForm onSubmit={handleEmailSubmit}>
            <FormTitle>Reset Your Password</FormTitle>
            <FormSubtitle>Enter your email address and we'll send you a verification code to reset your password.</FormSubtitle>
            
            <FormGroup>
              <Label htmlFor="email">Email Address</Label>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                disabled={isSubmitting}
              />
            </FormGroup>
            
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
            
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Reset Code'}
            </SubmitButton>
            
            <BackToLogin to="/login">Back to Login</BackToLogin>
          </EmailForm>
        ) : (
          <ResetForm onSubmit={handlePasswordReset}>
            <FormTitle>Enter Verification Code</FormTitle>
            <FormSubtitle>We've sent a verification code to {email}. Enter the code and your new password below.</FormSubtitle>
            
            <FormGroup>
              <Label htmlFor="code">Verification Code</Label>
              <Input
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength="6"
                disabled={isSubmitting}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={isSubmitting}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={isSubmitting}
              />
            </FormGroup>
            
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
            
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Resetting...' : 'Reset Password'}
            </SubmitButton>
            
            <ResendContainer>
              <ResendText>Didn't receive the code?</ResendText>
              <ResendButton type="button" onClick={handleResendCode} disabled={isSubmitting}>
                Resend Code
              </ResendButton>
            </ResendContainer>
            
            <BackToLogin to="/login">Back to Login</BackToLogin>
          </ResetForm>
        )}
      </ForgotPasswordCard>
    </ForgotPasswordContainer>
  );
};

// Styled components
const ForgotPasswordContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  padding: ${props => props.theme.spacing.lg};
`;

const ForgotPasswordCard = styled.div`
  background-color: ${props => props.theme.colors.verizonWhite};
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.large};
  width: 100%;
  max-width: 450px;
  padding: ${props => props.theme.spacing.xl};
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const OpplaneLogo = styled.img`
  height: 20px;
`;

const EmailForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const ResetForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormTitle = styled.h1`
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.fontSizes.xlarge};
  font-weight: 600;
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const FormSubtitle = styled.p`
  color: ${props => props.theme.colors.lightText};
  font-size: ${props => props.theme.fontSizes.medium};
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.fontSizes.medium};
  font-weight: 500;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: ${props => props.theme.fontSizes.medium};
  font-family: ${props => props.theme.fonts.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.1);
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.opplaneGray};
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.opplaneBlue};
  color: ${props => props.theme.colors.verizonWhite};
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.medium};
  font-weight: bold;
  cursor: pointer;
  transition: ${props => props.theme.transitions.short};
  margin-top: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.opplaneDarkBlue};
  }
  
  &:disabled {
    background-color: rgba(0, 102, 204, 0.5);
    cursor: not-allowed;
  }
`;

const ResendContainer = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ResendText = styled.span`
  color: ${props => props.theme.colors.lightText};
  font-size: ${props => props.theme.fontSizes.small};
  margin-right: ${props => props.theme.spacing.sm};
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-size: ${props => props.theme.fontSizes.small};
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  
  &:hover:not(:disabled) {
    color: ${props => props.theme.colors.opplaneDarkBlue};
  }
  
  &:disabled {
    color: ${props => props.theme.colors.lightText};
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const BackToLogin = styled(Link)`
  color: ${props => props.theme.colors.lightText};
  text-align: center;
  margin-top: ${props => props.theme.spacing.md};
  text-decoration: none;
  display: block;
  font-size: ${props => props.theme.fontSizes.small};
  
  &:hover {
    color: ${props => props.theme.colors.opplaneBlue};
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  color: ${props => props.theme.colors.error};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.medium};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSizes.small};
`;

const SuccessMessage = styled.div`
  background: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.3);
  color: ${props => props.theme.colors.success};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.medium};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSizes.small};
`;

export default ForgotPassword;
